/**
 * Native Resources
 *
 * Provides access to @classic-homes/native package documentation.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import nativeCatalog from '../data/native-catalog.json' with { type: 'json' };
import type { NativeCatalog } from '../types.js';

const catalog = nativeCatalog as unknown as NativeCatalog;

/**
 * Register native-related resources
 */
export function registerNativeResources(server: McpServer): void {
  // Full native catalog
  server.resource('Native Package', 'native://all', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog, null, 2),
      },
    ],
  }));

  // React hooks
  server.resource('Native React Hooks', 'native://react', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.react.importPath,
            description: catalog.subpackages.react.description,
            hooks: catalog.subpackages.react.hooks,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Svelte composables
  server.resource('Native Svelte Composables', 'native://svelte', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.svelte.importPath,
            description: catalog.subpackages.svelte.description,
            composables: catalog.subpackages.svelte.composables,
            functions: catalog.subpackages.svelte.functions,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Core functions
  server.resource('Native Core Functions', 'native://core', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.core.importPath,
            description: catalog.subpackages.core.description,
            functions: catalog.subpackages.core.functions,
            types: catalog.subpackages.core.types,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Features matrix
  server.resource('Native Features Matrix', 'native://features', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description: 'Native features with platform support and fallbacks',
            features: catalog.features,
          },
          null,
          2
        ),
      },
    ],
  }));

  // All types
  server.resource('Native Types', 'native://types', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.types, null, 2),
      },
    ],
  }));

  // Quick reference
  server.resource('Native Quick Reference', 'native://quick-reference', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'text/markdown',
        text: `# Native Package Quick Reference

## Installation

\`\`\`bash
${catalog.quickReference.installation}
\`\`\`

## Peer Dependency

\`${catalog.quickReference.peerDependency}\`

## Optional Plugins

Install only the Capacitor plugins you need:

\`\`\`bash
${catalog.quickReference.optionalPlugins.map((p) => `npm install ${p}`).join('\n')}
\`\`\`

## Basic Usage

\`\`\`typescript
${catalog.quickReference.basicUsage}
\`\`\`

## Features Matrix

| Feature | Hook | iOS | Android | Web Fallback |
|---------|------|-----|---------|--------------|
${catalog.features.map((f) => `| ${f.name} | \`${f.hook}\` | ${f.ios} | ${f.android} | ${f.webFallback} |`).join('\n')}

## React Hooks

${catalog.subpackages.react.hooks?.map((h) => `- \`${h.name}\` - ${h.description}`).join('\n')}

## Svelte Composables

${catalog.subpackages.svelte.composables?.map((c) => `- \`${c.name}\` - ${c.description}`).join('\n')}

## Platform Detection

\`\`\`typescript
import { isNative, isIOS, isAndroid, getPlatformInfo } from '@classic-homes/native';

if (isNative()) {
  const info = getPlatformInfo();
  console.log(info.platform); // 'ios' | 'android'
}
\`\`\`

## Error Handling

\`\`\`typescript
import {
  isPermissionDeniedError,
  isUserCancelledError,
  isFeatureUnavailableError,
} from '@classic-homes/native';

try {
  await getCurrentPosition();
} catch (error) {
  if (isPermissionDeniedError(error)) {
    // Show settings prompt
  } else if (isUserCancelledError(error)) {
    // User cancelled, no action needed
  } else if (isFeatureUnavailableError(error)) {
    // Feature not available on this device
  }
}
\`\`\`

## Conditional Rendering

### React

\`\`\`tsx
import { NativeGuard, usePlatformCheck } from '@classic-homes/native/react';

<NativeGuard native>
  <MobileFeatures />
</NativeGuard>

<NativeGuard platform="ios">
  <ApplePayButton />
</NativeGuard>

<NativeGuard feature="camera">
  <PhotoButton />
</NativeGuard>
\`\`\`

### Svelte

\`\`\`svelte
<script lang="ts">
  import { usePlatformCheck } from '@classic-homes/native/svelte';
  const platform = usePlatformCheck();
</script>

{#if platform.isNative}
  <MobileFeatures />
{/if}
\`\`\`
`,
      },
    ],
  }));
}
